import * as R from 'ramda';

import { Http } from 'src/shared/services/Http';

import { CPProgramWeek } from '../models/cpProgram';
import {
    ContentAnswer,
    ContentQuestion,
    FlexibilityHomeworkAnswer,
    FlexibilityHomeworkArea,
    SelectedAnswer,
    SurveyAnsweredContentQuestion,
    SurveyQuestion,
    SurveyQuestionAnswered,
    SurveyQuestionContentFetched,
    SurveyQuestionFetched,
} from '../weekFour/models/weekFour';
import { MindfulnessImprovementsTopics } from '../weekOne/models/weekOne';
import { CorePillarsRating, CorePillarsRatingRequestData } from '../weekShared/models/weekShared';
import {
    ActivitiesDataArguments,
    ActivitiesSelectionDomain,
    ActivitiesSelectionRequestData,
    ActivityData,
    LivingAccordingToValues,
    PlannedActivity,
} from '../weekThree/models/weekThree';
import { ValuesAssessmentDomain, ValuesAssessmentDomainRequested } from '../weekTwo/models/weekTwo';

export class CPProgramService {
    constructor(private http: Http) {}

    public formatSelectedAnswersData = (requestData: SelectedAnswer[]) => {
        const answeredQuestions = requestData.map(answer => ({
            question: answer.id,
            data: {
                option: answer.description,
                content: answer.answer || answer.additionalAnswer,
            },
        }));
        return answeredQuestions;
    };

    public getQuestionsFormattedAnswers = (questions: SurveyQuestionAnswered[]) => {
        const fetchedAnswers = questions.map(answer => ({
            id: answer.question,
            answer: answer.data.content,
            description: answer.data.option,
            additionalAnswer: answer.data.content,
        }));

        return fetchedAnswers;
    };

    public formatFetchedQuestion = (questions: SurveyQuestionFetched[]) => {
        const fetchedQuestions = questions.map(({ data, id }) => {
            if (data.content === '') {
                return {
                    id,
                    answer: data.content,
                    text: data.question,
                };
            }
            return {
                id,
                answers: data.options.map(description => ({
                    description,
                    isChosen: false,
                })),
                text: data.question,
            };
        });

        return fetchedQuestions;
    };

    public submitHomeworkConfidenceRating(value: number): Promise<number> {
        return Promise.resolve(value);
    }

    public submitLivingValuesData(
        livingAccordingToValues: LivingAccordingToValues,
    ): Promise<LivingAccordingToValues> {
        return Promise.resolve(livingAccordingToValues);
    }

    public async fetchCorePillarsRating(): Promise<CorePillarsRatingRequestData> {
        return this.http.GET<CorePillarsRatingRequestData>('programs/core-pillars/rating/latest/');
    }

    public submitCorePillarsRatingForm(
        weeks: CPProgramWeek[],
        rating: CorePillarsRating,
    ): Promise<Response> {
        // TODO: TYPES
        const requestData = weeks.reduce((acc, week) => {
            return {
                ...acc,
                [week.title]: rating[week.id],
            };
        }, []);

        return this.http.POST('programs/core-pillars/rating/', requestData);
    }

    public async submitMindfulnessImprovementsData(
        requestData: MindfulnessImprovementsTopics,
    ): Promise<MindfulnessImprovementsTopics> {
        try {
            const response = await this.http.POST<MindfulnessImprovementsTopics>(
                'programs/core-pillars/mindfulness-improvements/',
                requestData,
            );

            return response;
        } catch (error) {
            return Promise.reject(error);
        }
    }

    public async fetchMindfulnessImprovementsData(): Promise<MindfulnessImprovementsTopics> {
        try {
            return await this.http.GET<MindfulnessImprovementsTopics>(
                'programs/core-pillars/mindfulness-improvements/latest/',
            );
        } catch (error) {
            return Promise.reject(error);
        }
    }

    public submitValuesAssessmentsData(
        assessments: ValuesAssessmentDomain[],
    ): Promise<ValuesAssessmentDomain[]> {
        const requestedData = assessments.map(values => {
            return {
                ...values,
                domain: values.title,
            };
        });

        return this.http.POST('programs/core-pillars/values/', requestedData);
    }

    public async fetchValuesAssessmentsData(): Promise<ValuesAssessmentDomain[]> {
        const valuesAssessmentData = await this.http.GET<ValuesAssessmentDomainRequested[]>(
            'programs/core-pillars/values/',
        );

        return valuesAssessmentData.map(
            ({ domain, topics, additionalInfo, additionalRate, rate }) => ({
                title: domain,
                topics,
                additionalInfo,
                additionalRate,
                rate,
            }),
        );
    }

    public async fetchActivitiesSelectionData(): Promise<ActivitiesSelectionDomain[]> {
        const activitiesData = await this.http.GET<ActivitiesSelectionRequestData[]>(
            'programs/core-pillars/domains/',
        );

        return activitiesData.map(({ id, name, activities }) => ({
            id,
            activities,
            title: name,
        }));
    }

    public submitActivitiesSelectionData(
        chosenActivities: PlannedActivity[],
    ): Promise<ActivitiesDataArguments[]> {
        const requestData = chosenActivities.map(activity => ({
            domain: activity.domainId,
            dateToBePerformed: activity.dateToBePerformed,
            description: activity.title,
            isOther: activity.additionalTitle !== '',
            id: activity.id,
        }));

        return this.http.POST<ActivitiesDataArguments[]>(
            'programs/core-pillars/actions/',
            requestData,
        );
    }

    public fetchPlannedActivities(): Promise<ActivitiesDataArguments[]> {
        return this.http.GET<ActivitiesDataArguments[]>('survey/who5/actions/');
    }

    public async fetchFlexibilityExerciseQuestions(): Promise<SurveyQuestion[]> {
        const flexibilityExerciseData = await this.http.GET<SurveyQuestionFetched[]>(
            'programs/core-pillars/surveys/week-4-flexibility-exercise/questions/',
        );

        return this.formatFetchedQuestion(flexibilityExerciseData);
    }

    public async fetchFlexibilityExerciseAnswers(): Promise<SelectedAnswer[]> {
        const flexibilityExerciseData = await this.http.GET<SurveyQuestionAnswered[]>(
            'programs/core-pillars/surveys/week-4-flexibility-exercise/answers/latest/',
        );

        return this.getQuestionsFormattedAnswers(flexibilityExerciseData);
    }

    public submitFlexibilityExerciseAnswers(
        requestData: SelectedAnswer[],
    ): Promise<SurveyQuestionAnswered[]> {
        const answeredQuestions = this.formatSelectedAnswersData(requestData);

        return this.http.POST<SurveyQuestionAnswered[]>(
            'programs/core-pillars/surveys/week-4-flexibility-exercise/answers/',
            answeredQuestions,
        );
    }

    public submitFlexibilityPracticeAnswer(
        requestData: ContentAnswer,
    ): Promise<SurveyAnsweredContentQuestion[]> {
        const { id, answer } = requestData;

        return this.http.POST<SurveyAnsweredContentQuestion[]>(
            'programs/core-pillars/surveys/week-4-practice-survey/answers/',
            [{ question: id, data: { content: answer } }],
        );
    }

    public async fetchFlexibilityPracticeQuestion(): Promise<ContentQuestion> {
        const flexibilityPracticeQuestions = await this.http.GET<SurveyQuestionContentFetched[]>(
            'programs/core-pillars/surveys/week-4-practice-survey/questions/',
        );

        return flexibilityPracticeQuestions
            .map(({ id, data }) => ({
                id,
                text: data.question,
                answer: data.content,
            }))
            .reduce((acc, question) => ({
                ...acc,
                question,
            }));
    }

    public async fetchFlexibilityPracticeAnswer(): Promise<string> {
        const flexibilityPracticeAnswers = await this.http.GET<SurveyAnsweredContentQuestion[]>(
            'programs/core-pillars/surveys/week-4-practice-survey/answers/latest/',
        );

        const givenAnswer = R.head(flexibilityPracticeAnswers.map(answer => answer.data.content));

        return givenAnswer!;
    }

    public async fetchFlexibilityPlanQuestion(): Promise<SurveyQuestion[]> {
        const flexibilityPlanData = await this.http.GET<SurveyQuestionFetched[]>(
            'programs/core-pillars/surveys/week-4-flexibility-practice/questions/',
        );

        return this.formatFetchedQuestion(flexibilityPlanData);
    }

    public submitFlexibilityPlanData(
        requestData: SelectedAnswer[],
    ): Promise<SurveyQuestionAnswered[]> {
        const answeredQuestions = this.formatSelectedAnswersData(requestData);

        return this.http.POST<SurveyQuestionAnswered[]>(
            'programs/core-pillars/surveys/week-4-flexibility-practice/answers/',
            answeredQuestions,
        );
    }

    public async fetchFlexibilityPlanAnswers(): Promise<SelectedAnswer[]> {
        const flexibilityPlanData = await this.http.GET<SurveyQuestionAnswered[]>(
            'programs/core-pillars/surveys/week-4-flexibility-practice/answers/latest/',
        );

        return this.getQuestionsFormattedAnswers(flexibilityPlanData);
    }

    public async fetchFlexibilityHomeworkData(): Promise<FlexibilityHomeworkArea[]> {
        // TO BE IMPLEMENTED ON BACKEND (CURRENTLY 404)
        return [];
    }
    public submitFlexibilityHomeworkData(
        flexibilityHomework: ActivityData[],
    ): Promise<FlexibilityHomeworkAnswer[]> {
        const requestData = flexibilityHomework.map(activity => ({
            done: activity.completed,
            feelings: [activity.checkInActionsAnswer],
            description: activity.additionalTitle,
            scheduledActivity: activity.id,
        }));
        return this.http.POST<FlexibilityHomeworkAnswer[]>(
            'survey/who5/activity-assessments/',
            requestData,
        );
    }
}
